// PUSH-1a — the permission moment, and the device token.
//
// WHAT THIS WAVE DOES AND DOES NOT DO. It asks for the push permission at a
// moment that makes sense, stores a token, and removes it on sign out. NOTHING
// HERE SENDS. There is no APNs call in this file or anywhere in this wave: a
// token sitting in push_device_tokens with no sender is the correct end state,
// and a guard in the suite asserts it stays that way.
//
// NATIVE ONLY. The same bundle serves the web, and a browser must never be
// asked for notification permission by this code, never register, and never
// write a row. Every entry point below returns 'unsupported' off-native before
// it touches the plugin.
//
// ── WHY THE ASK IS NOT ON FIRST LAUNCH ────────────────────────────────────
// iOS shows the system dialog ONCE, EVER, per install. A merchant who taps Not
// Allow on a splash screen — before they know what Ghost CRM is — cannot be
// asked again by any code we write. The only way back is Settings, which
// almost nobody finds. So the ask is spent at the first moment the merchant
// can answer it knowingly:
//
//   TECHNICIAN: straight after they mark their first job complete. They have
//               just done the thing the app exists for, so "get told when a
//               job is assigned to you" is a sentence that means something.
//
//   OWNER:      an owner may never mark a job complete at all, so they get a
//               second path — turning push on in notification settings. That
//               is an explicit request, which is the strongest possible moment
//               to spend the one dialog on.
//
// BOTH PATHS CALL ensurePushRegistered. There is one ask in this codebase.

import { supabase } from './supabase'
import { isNativeShell } from './nativeShell'
import { BUILD_ID } from './loadFailure'

/**
 * 'unsupported' is the web, and also a native platform where the plugin is
 * missing. It is deliberately distinct from 'denied': nothing is wrong, there
 * is simply nothing to offer, and the UI must not tell a browser user their
 * push is switched off.
 */
export type PushState = 'unsupported' | 'prompt' | 'granted' | 'denied'

export interface PushIdentity {
  /** WHO to notify. Scoped to one sub_account, so the same human in two
   *  accounts is two members and two rows — their roles differ. */
  teamMemberId: string
  subAccountId: string
}

// ── DEVICE IDENTITY ───────────────────────────────────────────────────────
//
// WHY A GENERATED ID AND NOT A HARDWARE ONE. iOS gives no stable per-device
// identifier that survives a reinstall, and the ones it does give are either
// reset by the OS or off-limits. That is not a limitation to work around —
// it is exactly the behaviour we want:
//
//   A FRESH INSTALL IS A NEW DEVICE. New id, new row. The old row is left for
//   the sender's unregistered sweep to remove when APNs rejects its token,
//   which is the only authority on whether that install still exists.
//
//   A TOKEN ROTATION IS THE SAME DEVICE. Same id, and the upsert below hits
//   the (team_member_id, device_id) unique key and UPDATES. iOS rotates tokens
//   on restore and at its own discretion, so this is the common case, not the
//   edge case.

const DEVICE_ID_KEY = 'ghostcrm:push_device_id'

/** Stable for the life of the install. Generated once, then read. */
export function pushDeviceId(): string {
  try {
    const existing = window.localStorage.getItem(DEVICE_ID_KEY)
    if (existing) return existing
    const fresh = crypto.randomUUID()
    window.localStorage.setItem(DEVICE_ID_KEY, fresh)
    return fresh
  } catch {
    // Private mode, or storage full. A per-session id still registers and
    // still deregisters; it just looks like a new device next launch, which
    // is the safe direction to fail in.
    return crypto.randomUUID()
  }
}

// ── WHICH APNs HOST THIS INSTALL BELONGS TO ───────────────────────────────
//
// PUSH-1b2. A token is valid on exactly one of Apple's two hosts, and until
// this landed the sender had to guess: `platform` is only ios/android, and
// `app_build` is the WEB bundle's commit sha, so a debug install and a
// TestFlight install of the same commit were indistinguishable.
//
// NOTHING IN CAPACITOR ANSWERS THIS. No API on @capacitor/core, /app or
// /push-notifications reports the build configuration, the signing identity or
// the entitlements, and the JS bundle is byte-identical across configurations
// because `cap sync` copies one dist into all of them.
//
// SO IT COMES FROM NATIVE. GhostBridgeViewController (AppDelegate.swift) reads
// the `aps-environment` entitlement out of the embedded provisioning profile —
// the same value APNs itself uses to decide which host minted the token — and
// injects it at document start, before any app code runs.
//
// A GLOBAL SECRET IS NOT A SUBSTITUTE. One project-wide value is wrong the
// moment a debug install and a TestFlight install both exist, which is exactly
// launch week. This is a property of the install, so it travels on the row.

export type ApnsEnv = 'sandbox' | 'production'

/**
 * null means "we do not know", and that is a real answer, not a failure:
 * the web, or a native binary built before this shipped. The row stores NULL
 * and the sender keeps its cross-host probe for exactly those devices. It is
 * never defaulted to a host nobody verified.
 */
export function apnsEnvironment(): ApnsEnv | null {
  if (!isNativeShell()) return null
  const injected = (window as unknown as { __GHOST_APNS_ENV__?: unknown }).__GHOST_APNS_ENV__
  return injected === 'sandbox' || injected === 'production' ? injected : null
}

// push_device_tokens landed in 20261094 and is not in the generated Database
// typings yet (they regenerate on a separate cadence), so the client is cast
// locally rather than loosening the typed client for the rest of the app.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sb = supabase as any

async function loadPlugin() {
  const { PushNotifications } = await import('@capacitor/push-notifications')
  return PushNotifications
}

/**
 * What iOS currently thinks, with no side effect and no dialog.
 *
 * 'prompt' is the ONLY state in which the system has never asked, which is why
 * it is the whole of the never-ask-twice rule below.
 */
export async function pushPermissionState(): Promise<PushState> {
  if (!isNativeShell()) return 'unsupported'
  try {
    const PushNotifications = await loadPlugin()
    const { receive } = await PushNotifications.checkPermissions()
    if (receive === 'granted') return 'granted'
    if (receive === 'denied') return 'denied'
    return 'prompt'
  } catch {
    return 'unsupported'
  }
}

// ── TOKEN CAPTURE ─────────────────────────────────────────────────────────
//
// ONE LONG-LIVED LISTENER, NOT ONE PER CALL. APNs delivers a token
// asynchronously after register(), and delivers a NEW one whenever it rotates
// — which can happen at any launch, with nobody having asked. A listener
// installed once and left running is the only thing that catches a rotation
// that arrives while the app is already open.

let watching = false
let watchedIdentity: PushIdentity | null = null

/**
 * Persist the token for THIS member on THIS device.
 *
 * The upsert targets the (team_member_id, device_id) unique key, so a rotated
 * token updates the existing row rather than adding a second one. RLS already
 * restricts the write to the caller's own member rows; the explicit ids here
 * are what the row is keyed BY, not what authorises it.
 */
async function storeToken(identity: PushIdentity, token: string): Promise<void> {
  const { error } = await sb.from('push_device_tokens').upsert(
    {
      team_member_id: identity.teamMemberId,
      sub_account_id: identity.subAccountId,
      device_id: pushDeviceId(),
      token,
      platform: 'ios',
      app_build: BUILD_ID,
      // PUSH-1b2: which of Apple's two hosts this token is valid on, read from
      // the signing entitlement. NULL when unknown, never a default.
      apns_env: apnsEnvironment(),
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'team_member_id,device_id' },
  )
  if (error) {
    // Not fatal and not shown. Failing to store a token means notifications do
    // not arrive; it must never break the screen the merchant was using.
    console.warn('[push] could not store device token', error)
  }
}

/**
 * Install the token listeners and, if permission is already granted, ask iOS
 * for a token. NEVER SHOWS A DIALOG — it is safe to call on every launch.
 *
 * This is the token-rotation path. It is idempotent: called twice, it installs
 * one set of listeners and simply re-points them at the current identity,
 * which matters when a merchant switches sub_account without a reload.
 */
export async function startPushTokenWatch(identity: PushIdentity): Promise<void> {
  if (!isNativeShell()) return
  watchedIdentity = identity
  try {
    const PushNotifications = await loadPlugin()

    if (!watching) {
      watching = true
      await PushNotifications.addListener('registration', (t: { value: string }) => {
        const who = watchedIdentity
        if (!who || !t?.value) return
        void storeToken(who, t.value)
      })
      await PushNotifications.addListener('registrationError', (e: unknown) => {
        // A token we never got is a notification that never arrives. It is not
        // something to interrupt the merchant about.
        console.warn('[push] registration failed', e)
      })
    }

    const { receive } = await PushNotifications.checkPermissions()
    if (receive === 'granted') await PushNotifications.register()
  } catch (e) {
    console.warn('[push] token watch unavailable', e)
  }
}

/**
 * Is there a token for THIS device right now?
 *
 * Distinct from the permission, and deliberately so. A merchant can leave iOS
 * permission granted and still switch push off for this handset, which deletes
 * the token — so "granted" alone would light up a switch for a device that
 * receives nothing. RLS scopes the read to the caller's own rows.
 */
export async function isThisDeviceRegistered(): Promise<boolean> {
  if (!isNativeShell()) return false
  try {
    const { data, error } = await sb
      .from('push_device_tokens')
      .select('id')
      .eq('device_id', pushDeviceId())
      .limit(1)
    if (error) return false
    return Array.isArray(data) && data.length > 0
  } catch {
    return false
  }
}

// ── THE ASK ───────────────────────────────────────────────────────────────

/** Shared by both paths, so two of them cannot race into two dialogs. */
let askInFlight: Promise<PushState> | null = null

/**
 * THE ONE PERMISSION MOMENT. Called by the technician path (first completed
 * job) and the owner path (notification settings), and by nothing else.
 *
 * IT WILL NOT ASK TWICE. The gate is iOS's own state, which is the only
 * authority: 'prompt' means the system dialog has never been shown on this
 * install, and after it has been shown the state is permanently 'granted' or
 * 'denied'. Calling requestPermissions() again in either of those states does
 * NOTHING VISIBLE — no dialog, no change — which to a merchant tapping a
 * switch reads exactly like a bug. So we never make that call.
 *
 * A DENIED RESULT IS FINAL and is returned as such, for the caller to say so
 * honestly rather than leave a dead control on screen.
 */
export async function ensurePushRegistered(identity: PushIdentity): Promise<PushState> {
  if (!isNativeShell()) return 'unsupported'
  if (askInFlight) return askInFlight

  askInFlight = (async () => {
    try {
      const PushNotifications = await loadPlugin()
      const current = await PushNotifications.checkPermissions()

      // Already answered. Register (harmless, and catches a rotation) or stop.
      if (current.receive === 'denied') return 'denied'
      if (current.receive === 'granted') {
        await startPushTokenWatch(identity)
        return 'granted'
      }

      // Never asked. This is the one dialog we get.
      const asked = await PushNotifications.requestPermissions()
      if (asked.receive !== 'granted') return 'denied'
      await startPushTokenWatch(identity)
      return 'granted'
    } catch (e) {
      console.warn('[push] permission flow unavailable', e)
      return 'unsupported'
    } finally {
      askInFlight = null
    }
  })()

  return askInFlight
}

// ── SIGN OUT: THE SHARED CREW PHONE ───────────────────────────────────────
//
// THE LEAK, PLAINLY. A yard phone is signed in as one technician, they leave,
// the next technician signs in. Without this, the departed technician's row
// still points at that handset's token, and every notification meant for them
// — a customer's reply, a job assignment — lights up on a phone belonging to
// somebody else. It keeps working indefinitely, because APNs has no idea the
// human changed.
//
// SO THE ROW IS DELETED, BEFORE THE SESSION GOES. Not marked stale, not left
// for the sender to clean up: deleted, while auth.uid() still exists, because
// after signOut() the RLS delete policy matches nothing and the row is
// stranded for good.
//
// IT DELETES BY DEVICE AND LETS RLS DO THE SCOPING. The filter is device_id
// alone; the own-rows delete policy narrows it to the signing-out member's own
// rows. So a DIFFERENT member's row for this same handset is invisible here
// and survives untouched — which is correct. A client that can delete other
// people's tokens is a client that can silence them.

export async function deregisterThisDevice(): Promise<void> {
  if (!isNativeShell()) return
  try {
    const { error } = await sb.from('push_device_tokens').delete().eq('device_id', pushDeviceId())
    if (error) console.warn('[push] could not deregister device', error)
  } catch (e) {
    console.warn('[push] deregister failed', e)
  }
  // Stop the listeners answering for a member who is on their way out.
  watchedIdentity = null
}

/**
 * THE ONLY WAY OUT OF THE APP.
 *
 * Every sign-out button routes through here rather than calling
 * supabase.auth.signOut() directly, because the deregistration has to happen
 * FIRST and a call site that forgets it leaks a technician's notifications
 * onto a shared phone. A guard in the suite fails the build if a raw
 * supabase.auth.signOut() is reintroduced anywhere else.
 */
export async function signOutEverywhere(): Promise<void> {
  await deregisterThisDevice()
  await supabase.auth.signOut()
}

/** Test seam. Resets module state between cases; no production caller. */
export function __resetPushStateForTests(): void {
  watching = false
  watchedIdentity = null
  askInFlight = null
}
